/**
 * BoutListPrint — Print-only bout list layout.
 * Renders a grid table grouped by day and optionally by ring.
 * Hidden on screen (print-only CSS class), shown when browser prints.
 */
import GenderBadge from '../shared/GenderBadge';
import { ClockIcon } from '../shared/Icons';
import { useI18n } from '../../i18n';
import StagePill from './StagePill';
import { deriveAgeGroup, deriveWeightClass, roundFormula } from '../../utils/tournament';
import { cornerName } from '../../utils/personName';
import type { Bout } from '../../types';
import { BADGE_XXS } from '../shared/badgeSize';

interface Props {
  bouts: Bout[];      // All bouts (unfiltered — print shows everything)
  allBouts: Bout[];   // Full bout list for source bout lookups
  allScored: boolean;
  /** The Stage column (QF, SF, Final). An open ring has only Challenge bouts
   *  and hides it; a championship and a hybrid have brackets (#536). */
  showStage: boolean;
}

export default function BoutListPrint({ bouts, allBouts, allScored, showStage }: Props) {
  const { t } = useI18n();

  const sortedBouts = [...bouts].sort((a, b) => a.bout_number - b.bout_number);
  const days = [...new Set(sortedBouts.map(b => b.bout_date ? String(b.bout_date).slice(0, 10) : '').filter((s): s is string => !!s))].sort();
  const ringNames = [...new Set(sortedBouts.map(b => b.ring_name).filter((s): s is string => !!s))].sort();
  const multiRing = ringNames.length > 1;

  const colWidths: string[] = [];
  if (multiRing) colWidths.push('1.2rem');           // ring index within day
  colWidths.push('1.5rem');                           // bout #
  if (showStage) colWidths.push('5rem');           // stage
  colWidths.push('2rem', '2.5rem', '3rem', '3rem', '1fr', 'auto', '1fr', 'auto');
  const headerCols = colWidths.join(' ');

  function renderBoutRow(bout: Bout, ringIdx: number) {
    const ag = bout.bout_age_group || deriveAgeGroup(bout.red_age_group, bout.blue_age_group);
    const wc = bout.bout_weight_class || deriveWeightClass(bout.red_weight_class, bout.blue_weight_class);
    const tf = roundFormula(ag, bout.rounds); // #602: 3 or 5 rounds
    const redName = bout.red_first_name
      ? <><span className="font-semibold">{cornerName(bout, 'red')}</span>{bout.red_team_name && <span className="text-gray-500 font-normal ml-0.5">({bout.red_team_name})</span>}</>
      : bout.red_source_bout_id ? <span>{t.common.boutWinner.replace('{n}', String(allBouts.find(b2 => b2.id === bout.red_source_bout_id)?.bout_number ?? '?'))}</span> : <span>{t.common.tbd}</span>;
    const blueName = bout.blue_first_name
      ? <><span className="font-semibold">{cornerName(bout, 'blue')}</span>{bout.blue_team_name && <span className="text-gray-500 font-normal ml-0.5">({bout.blue_team_name})</span>}</>
      : bout.blue_source_bout_id ? <span>{t.common.boutWinner.replace('{n}', String(allBouts.find(b2 => b2.id === bout.blue_source_bout_id)?.bout_number ?? '?'))}</span> : <span>{t.common.tbd}</span>;

    return (
      <div key={bout.id} className="bout-card-print hidden items-center py-0.5 px-1 border-b border-gray-200 text-[10px]"
        style={{gridTemplateColumns: headerCols}}>
        {multiRing && <span className="text-gray-400 text-right">{ringIdx}</span>}
        <span className="font-bold text-right">{bout.bout_number}</span>
        {/* #458 — the cell stays for the grid; a bout with no stage has no pill. */}
        {showStage && <span className="text-center"><StagePill stage={bout.stage} size="print" /></span>}
        <span className="text-center">{(() => { const g = (bout.red_gender || bout.blue_gender || '').toLowerCase(); return g ? <GenderBadge gender={g} short size="xs" className="px-1" /> : null; })()}</span>
        <span className="text-center">{ag && <span className={`badge badge-gray ${BADGE_XXS} px-1`}>{ag}</span>}</span>
        <span className="text-center">{wc && <span className={`badge badge-gray ${BADGE_XXS} px-1`}>{wc}</span>}</span>
        <span className="text-center text-gray-500">{tf || ''}</span>
        <span className="text-red-700 text-right truncate">{redName}</span>
        <span className="text-gray-400 text-center px-1">{t.common.vs}</span>
        <span className="text-blue-700 text-left truncate">{blueName}</span>
        <span className="text-right">
          {allScored && bout.verdict_winner && (
            <span className={`font-semibold ${bout.verdict_winner === 'red' ? 'text-red-700' : bout.verdict_winner === 'blue' ? 'text-blue-700' : ''}`}>
              {bout.verdict_outcome === 'DRAW' ? 'DRAW' : (
                <>
                  {bout.verdict_winner === 'red' ? t.common.red : bout.verdict_winner === 'blue' ? t.common.blue : t.common.draw}
                  {bout.verdict_outcome && ` ${bout.verdict_outcome}`}
                </>
              )}
            </span>
          )}
        </span>
      </div>
    );
  }

  function renderHeader() {
    return (
      <div className="bout-card-print hidden items-center py-0.5 px-1 border-b-2 border-gray-300 text-[9px] font-bold uppercase tracking-wide text-gray-400"
        style={{gridTemplateColumns: headerCols}}>
        {multiRing && <span className="text-right"></span>}
        <span className="text-right">#</span>
        {showStage && <span className="text-center">{t.common.stage}</span>}
        <span className="text-center">{t.common.gender}</span>
        <span className="text-center">{t.common.age}</span>
        <span className="text-center">{t.common.weight}</span>
        <span className="text-center inline-flex justify-center"><ClockIcon className="w-3.5 h-3.5" /></span>
        <span className="text-center text-red-600">{t.common.red}</span>
        <span className="text-center"></span>
        <span className="text-center text-blue-600">{t.common.blue}</span>
        <span className="text-right">{allScored ? t.common.result : ''}</span>
      </div>
    );
  }

  return (
    <div className="print-only" style={{display:'none'}}>
      {days.map(day => {
        const dayBouts = sortedBouts.filter(b => b.bout_date && String(b.bout_date).slice(0, 10) === day);
        return (
          <div key={day}>
            <div className="flex items-center gap-3 py-1.5">
              <div className="flex-1 h-px bg-gray-300" />
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                {day} ({dayBouts.length})
              </span>
              <div className="flex-1 h-px bg-gray-300" />
            </div>
            {multiRing ? ringNames.map(ring => {
              const ringBouts = dayBouts.filter(b => b.ring_name === ring);
              if (!ringBouts.length) return null;
              return (
                <div key={ring} className="mb-2">
                  <div className="text-[10px] font-bold text-gray-600 px-1 py-1 bg-gray-100 border-b border-gray-300">{ring} ({ringBouts.length})</div>
                  {renderHeader()}
                  {ringBouts.map((bout, i) => renderBoutRow(bout, i + 1))}
                </div>
              );
            }) : (
              <>
                {renderHeader()}
                {dayBouts.map((bout, i) => renderBoutRow(bout, i + 1))}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
